import React, { useState } from 'react';
import { 
  ArrowLeft, 
  ArrowRight, 
  CheckCircle, 
  DollarSign, 
  Target, 
  Calendar, 
  TrendingUp, 
  Shield, 
  BarChart3,
  Lightbulb,
  Users,
  PieChart,
  AlertCircle,
  Star
} from 'lucide-react';

interface InvestmentProfile {
  amount: string;
  timeline: string;
  riskTolerance: string;
  objectives: string[];
  experience: string;
  situation: string;
}

interface GuidedInvestmentProps {
  onNavigateBack: () => void;
}

const GuidedInvestment: React.FC<GuidedInvestmentProps> = ({ onNavigateBack }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [profile, setProfile] = useState<InvestmentProfile>({
    amount: '',
    timeline: '',
    riskTolerance: '',
    objectives: [],
    experience: '',
    situation: ''
  });

  const steps = [
    { title: "Montant à investir", icon: DollarSign },
    { title: "Horizon temporel", icon: Calendar },
    { title: "Profil de risque", icon: TrendingUp },
    { title: "Objectifs", icon: Target },
    { title: "Expérience", icon: BarChart3 },
    { title: "Situation", icon: Users },
    { title: "Recommandation", icon: Star }
  ];

  const getRecommendation = () => {
    if (profile.riskTolerance === 'conservateur') {
      return {
        strategy: 'Protecteur',
        allocation: { gfi: 20, scpi: 50, sukuk: 30 },
        expectedReturn: '3.5%',
        description: 'Stratégie sécurisée privilégiant la préservation du capital',
        color: 'emerald'
      };
    } else if (profile.riskTolerance === 'modere') {
      return {
        strategy: 'Équilibré',
        allocation: { gfi: 40, scpi: 35, sukuk: 25 },
        expectedReturn: '5.5%',
        description: 'Équilibre optimal entre sécurité et performance',
        color: 'blue'
      };
    } else {
      return {
        strategy: 'Dynamique',
        allocation: { gfi: 60, scpi: 25, sukuk: 15 },
        expectedReturn: '8.5%',
        description: 'Stratégie ambitieuse pour maximiser les gains',
        color: 'orange'
      };
    }
  };

  const nextStep = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const updateProfile = (field: keyof InvestmentProfile, value: string | string[]) => {
    setProfile(prev => ({ ...prev, [field]: value }));
  };

  const addObjective = (objective: string) => {
    if (!profile.objectives.includes(objective)) {
      updateProfile('objectives', [...profile.objectives, objective]);
    }
  };

  const removeObjective = (objective: string) => {
    updateProfile('objectives', profile.objectives.filter(obj => obj !== objective));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-6xl mx-auto px-4 py-6">
          <button 
            onClick={onNavigateBack}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Retour à l'accueil
          </button>
          
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Guide d'Investissement Personnalisé</h1>
              <p className="text-gray-600 mt-2">Découvrez la stratégie d'investissement parfaite pour votre profil</p>
            </div>
            
            <div className="text-right">
              <div className="text-sm text-gray-500">Étape {currentStep + 1} sur {steps.length}</div>
              <div className="text-2xl font-bold text-blue-600">{Math.round((currentStep + 1) / steps.length * 100)}%</div>
            </div>
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="bg-white border-b">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex items-center py-6">
            {steps.map((step, index) => {
              const Icon = step.icon;
              const isCompleted = index < currentStep;
              const isCurrent = index === currentStep;
              
              return (
                <div key={index} className="flex items-center flex-1">
                  <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 ${
                    isCompleted 
                      ? 'bg-green-500 border-green-500 text-white' 
                      : isCurrent 
                        ? 'bg-blue-500 border-blue-500 text-white' 
                        : 'bg-gray-100 border-gray-300 text-gray-400'
                  }`}>
                    {isCompleted ? <CheckCircle className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
                  </div>
                  
                  <div className="ml-3 flex-1">
                    <div className={`text-sm font-medium ${isCurrent ? 'text-blue-600' : isCompleted ? 'text-green-600' : 'text-gray-400'}`}>
                      {step.title}
                    </div>
                    {index < steps.length - 1 && (
                      <div className={`h-0.5 mt-2 ${isCompleted ? 'bg-green-500' : 'bg-gray-200'}`}></div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          
          {/* Step 0: Amount */}
          {currentStep === 0 && (
            <div className="text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <DollarSign className="w-10 h-10 text-green-600" />
              </div>
              
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Quel montant souhaitez-vous investir ?</h2>
              <p className="text-gray-600 mb-8">Cette information nous aidera à vous proposer les meilleures options d'investissement</p>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-2xl mx-auto">
                {[
                  { label: "Moins de 10K€", value: "moins_10k" },
                  { label: "10K€ - 50K€", value: "10k_50k" },
                  { label: "50K€ - 100K€", value: "50k_100k" },
                  { label: "Plus de 100K€", value: "plus_100k" }
                ].map((option) => (
                  <button
                    key={option.value}
                    onClick={() => updateProfile('amount', option.value)}
                    className={`p-6 rounded-xl border-2 transition-all duration-200 ${
                      profile.amount === option.value
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50'
                    }`}
                  >
                    <div className="font-semibold text-lg">{option.label}</div>
                  </button>
                ))}
              </div>
              
              <div className="mt-8">
                <input
                  type="text"
                  placeholder="Ou saisissez un montant personnalisé"
                  className="w-full max-w-md mx-auto px-4 py-3 border border-gray-300 rounded-lg text-center text-lg"
                  onChange={(e) => updateProfile('amount', e.target.value)}
                />
              </div>
            </div>
          )}

          {/* Step 1: Timeline */}
          {currentStep === 1 && (
            <div className="text-center">
              <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Calendar className="w-10 h-10 text-blue-600" />
              </div>
              
              <h2 className="text-3xl font-bold text-gray-900 mb-4"></h2>
              <p className="text-gray-600 mb-8">L'horizon de placement détermine le type d'investissement le plus adapté</p>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {[
                  { 
                    label: "Court terme", 
                    value: "court", 
                    duration: "1-3 ans",
                    description: "Liquidité importante, faible risque",
                    color: "green"
                  },
                  { 
                    label: "Moyen terme", 
                    value: "moyen", 
                    duration: "3-7 ans",
                    description: "Équilibre performance/sécurité",
                    color: "blue"
                  },
                  { 
                    label: "Long terme", 
                    value: "long", 
                    duration: "7+ ans",
                    description: "Potentiel de croissance maximal",
                    color: "orange"
                  }
                ].map((option) => (
                  <button
                    key={option.value}
                    onClick={() => updateProfile('timeline', option.value)}
                    className={`p-6 rounded-xl border-2 transition-all duration-200 ${
                      profile.timeline === option.value
                        ? `border-${option.color}-500 bg-${option.color}-50 text-${option.color}-700`
                        : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50'
                    }`}
                  >
                    <div className="font-bold text-xl mb-2">{option.label}</div>
                    <div className="text-lg font-semibold mb-2">{option.duration}</div>
                    <div className="text-sm text-gray-600">{option.description}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 2: Risk Tolerance */}
          {currentStep === 2 && (
            <div className="text-center">
              <div className="w-20 h-20 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <TrendingUp className="w-10 h-10 text-orange-600" />
              </div>
              
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Quel est votre profil de risque ?</h2>
              <p className="text-gray-600 mb-8">Évaluons votre appétence au risque pour optimiser vos rendements</p>
              
              <div className="space-y-6">
                {[
                  {
                    label: "Conservateur",
                    value: "conservateur",
                    icon: Shield,
                    description: "Je privilégie la sécurité de mon capital, même si les gains sont modestes",
                    risk: "Faible",
                    return: "3-4%",
                    color: "green"
                  },
                  {
                    label: "Modéré",
                    value: "modere", 
                    icon: BarChart3,
                    description: "J'accepte une prise de risque limitée pour obtenir des rendements intéressants",
                    risk: "Modéré",
                    return: "5-6%",
                    color: "blue"
                  },
                  {
                    label: "Dynamique",
                    value: "dynamique",
                    icon: TrendingUp,
                    description: "Je suis prêt(e) à prendre des risques pour maximiser mes gains potentiels",
                    risk: "Élevé",
                    return: "7-10%",
                    color: "orange"
                  }
                ].map((option) => {
                  const Icon = option.icon;
                  return (
                    <button
                      key={option.value}
                      onClick={() => updateProfile('riskTolerance', option.value)}
                      className={`w-full p-6 rounded-xl border-2 transition-all duration-200 text-left ${
                        profile.riskTolerance === option.value
                          ? `border-${option.color}-500 bg-${option.color}-50`
                          : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50'
                      }`}
                    >
                      <div className="flex items-start space-x-4">
                        <div className={`w-12 h-12 bg-${option.color}-100 rounded-lg flex items-center justify-center`}>
                          <Icon className={`w-6 h-6 text-${option.color}-600`} />
                        </div>
                        
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-2">
                            <h3 className="font-bold text-xl text-gray-900">{option.label}</h3>
                            <div className="flex space-x-4">
                              <span className="text-sm bg-gray-100 px-3 py-1 rounded-full">Risque: {option.risk}</span>
                              <span className="text-sm bg-green-100 text-green-700 px-3 py-1 rounded-full">Rendement: {option.return}</span>
                            </div>
                          </div>
                          <p className="text-gray-600">{option.description}</p>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Step 3: Objectives */}
          {currentStep === 3 && (
            <div className="text-center">
              <div className="w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Target className="w-10 h-10 text-purple-600" />
              </div>
              
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Quels sont vos objectifs d'investissement ?</h2>
              <p className="text-gray-600 mb-8">Vous pouvez sélectionner plusieurs objectifs</p>
              
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {[
                  { label: "Préparer ma retraite", value: "retraite", icon: "🏖️" },
                  { label: "Acheter un bien immobilier", value: "immobilier", icon: "🏠" },
                  { label: "Financer les études", value: "etudes", icon: "🎓" },
                  { label: "Constituer un patrimoine", value: "patrimoine", icon: "💎" },
                  { label: "Générer des revenus", value: "revenus", icon: "💰" },
                  { label: "Projet entrepreneurial", value: "entreprise", icon: "🚀" }
                ].map((option) => (
                  <button
                    key={option.value}
                    onClick={() => 
                      profile.objectives.includes(option.value) 
                        ? removeObjective(option.value)
                        : addObjective(option.value)
                    }
                    className={`p-4 rounded-xl border-2 transition-all duration-200 ${
                      profile.objectives.includes(option.value)
                        ? 'border-purple-500 bg-purple-50 text-purple-700'
                        : 'border-gray-200 hover:border-purple-300 hover:bg-purple-50'
                    }`}
                  >
                    <div className="text-2xl mb-2">{option.icon}</div>
                    <div className="font-semibold">{option.label}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 4: Experience */}
          {currentStep === 4 && (
            <div className="text-center">
              <div className="w-20 h-20 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <BarChart3 className="w-10 h-10 text-indigo-600" />
              </div>
              
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Quelle est votre expérience en investissement ?</h2>
              <p className="text-gray-600 mb-8">Cette information nous permet de personnaliser nos recommandations</p>
              
              <div className="space-y-4">
                {[
                  { label: "Débutant", value: "debutant", description: "Je n'ai jamais investi ou très peu d'expérience" },
                  { label: "Intermédiaire", value: "intermediaire", description: "J'ai quelques connaissances et expériences d'investissement" },
                  { label: "Expérimenté", value: "experimente", description: "Je maîtrise les différents types d'investissements" },
                  { label: "Expert", value: "expert", description: "J'ai une expertise approfondie des marchés financiers" }
                ].map((option) => (
                  <button
                    key={option.value}
                    onClick={() => updateProfile('experience', option.value)}
                    className={`w-full p-6 rounded-xl border-2 transition-all duration-200 text-left ${
                      profile.experience === option.value
                        ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                        : 'border-gray-200 hover:border-indigo-300 hover:bg-indigo-50'
                    }`}
                  >
                    <div className="font-bold text-lg mb-2">{option.label}</div>
                    <div className="text-gray-600">{option.description}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 5: Situation */}
          {currentStep === 5 && (
            <div className="text-center">
              <div className="w-20 h-20 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Users className="w-10 h-10 text-teal-600" />
              </div>
              
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Quelle est votre situation actuelle ?</h2>
              <p className="text-gray-600 mb-8">Votre situation personnelle influence le choix de vos investissements</p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {[
                  { label: "Célibataire sans enfant", value: "celibataire", icon: "👤" },
                  { label: "En couple sans enfant", value: "couple", icon: "👥" },
                  { label: "Famille avec enfants", value: "famille", icon: "👨‍👩‍👧‍👦" },
                  { label: "Proche de la retraite", value: "retraite", icon: "👴" }
                ].map((option) => (
                  <button
                    key={option.value}
                    onClick={() => updateProfile('situation', option.value)}
                    className={`p-6 rounded-xl border-2 transition-all duration-200 ${
                      profile.situation === option.value
                        ? 'border-teal-500 bg-teal-50 text-teal-700'
                        : 'border-gray-200 hover:border-teal-300 hover:bg-teal-50'
                    }`}
                  >
                    <div className="text-3xl mb-3">{option.icon}</div>
                    <div className="font-semibold text-lg">{option.label}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 6: Recommendation */}
          {currentStep === 6 && (
            <div>
              {(() => {
                const recommendation = getRecommendation();
                return (
                  <div className="text-center">
                    <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6">
                      <Star className="w-10 h-10 text-yellow-600" />
                    </div>
                    
                    <h2 className="text-3xl font-bold text-gray-900 mb-4">Votre Stratégie Personnalisée</h2>
                    <p className="text-gray-600 mb-8">Basée sur votre profil, voici notre recommandation</p>
                    
                    <div className={`bg-${recommendation.color}-50 border border-${recommendation.color}-200 rounded-2xl p-8 mb-8`}>
                      <h3 className={`text-2xl font-bold text-${recommendation.color}-700 mb-4`}>
                        Profil {recommendation.strategy}
                      </h3>
                      <div className={`text-4xl font-bold text-${recommendation.color}-600 mb-2`}>
                        {recommendation.expectedReturn}
                      </div>
                      <p className="text-gray-600 mb-6">{recommendation.description}</p>
                      
                      {/* Allocation Chart */}
                      <div className="bg-white rounded-xl p-6 mb-6">
                        <h4 className="font-bold text-lg mb-4">Répartition Recommandée</h4>
                        <div className="space-y-4">
                          <div className="flex items-center justify-between">
                            <span className="text-gray-700">GFI (Fonds de placement)</span>
                            <span className="font-bold">{recommendation.allocation.gfi}%</span>
                            <div className="flex-1 mx-4 bg-gray-200 rounded-full h-2">
                              <div 
                                className="bg-blue-500 h-2 rounded-full" 
                                style={{width: `${recommendation.allocation.gfi}%`}}
                              ></div>
                            </div>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-gray-700">SCPI (Immobilier locatif)</span>
                            <span className="font-bold">{recommendation.allocation.scpi}%</span>
                            <div className="flex-1 mx-4 bg-gray-200 rounded-full h-2">
                              <div 
                                className="bg-green-500 h-2 rounded-full" 
                                style={{width: `${recommendation.allocation.scpi}%`}}
                              ></div>
                            </div>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-gray-700">Sukuk (Finance islamique)</span>
                            <span className="font-bold">{recommendation.allocation.sukuk}%</span>
                            <div className="flex-1 mx-4 bg-gray-200 rounded-full h-2">
                              <div 
                                className="bg-orange-500 h-2 rounded-full" 
                                style={{width: `${recommendation.allocation.sukuk}%`}}
                              ></div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                      <div className="bg-green-50 p-6 rounded-xl">
                        <Lightbulb className="w-8 h-8 text-green-600 mb-3" />
                        <h4 className="font-bold text-gray-900 mb-2">Conseil Personnalisé</h4>
                        <p className="text-sm text-gray-600">
                          Diversifiez vos investissements pour réduire les risques
                        </p>
                      </div>
                      
                      <div className="bg-blue-50 p-6 rounded-xl">
                        <PieChart className="w-8 h-8 text-blue-600 mb-3" />
                        <h4 className="font-bold text-gray-900 mb-2">Suivi Recommandé</h4>
                        <p className="text-sm text-gray-600">
                          Révision de votre portefeuille tous les 6 mois
                        </p>
                      </div>
                      
                      <div className="bg-orange-50 p-6 rounded-xl">
                        <AlertCircle className="w-8 h-8 text-orange-600 mb-3" />
                        <h4 className="font-bold text-gray-900 mb-2">Point d'Attention</h4>
                        <p className="text-sm text-gray-600">
                          Gardez toujours une épargne de précaution
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                      <button className="bg-blue-600 text-white px-8 py-4 rounded-xl font-bold hover:bg-blue-700 transition-colors">
                        Commencer mon investissement
                      </button>
                      <button className="border-2 border-blue-600 text-blue-600 px-8 py-4 rounded-xl font-bold hover:bg-blue-50 transition-colors">
                        Parler à un conseiller
                      </button>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}

          {/* Navigation */}
          <div className="flex justify-between mt-12 pt-8 border-t">
            <button
              onClick={prevStep}
              disabled={currentStep === 0}
              className={`flex items-center px-6 py-3 rounded-xl font-semibold transition-colors ${
                currentStep === 0 
                  ? 'text-gray-400 cursor-not-allowed' 
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              }`}
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Précédent
            </button>
            
            <button
              onClick={nextStep}
              disabled={currentStep === steps.length - 1}
              className={`flex items-center px-6 py-3 rounded-xl font-semibold transition-colors ${
                currentStep === steps.length - 1
                  ? 'text-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              Suivant
              <ArrowRight className="w-5 h-5 ml-2" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GuidedInvestment;